import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Loader2, Plus, ShieldAlert } from "lucide-react";
import {
  ACCENT, CONTROL_COLOR, SEIZURE_TYPES, SEIZURE_TRIGGER_LIST,
  fmtDate, humanize, Field, HistoryTable,
} from "./shared";

export default function SeizureTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    seizure_type: "focal_aware",
    duration_seconds: 60,
    triggers: [],
    post_ictal_minutes: 5,
    medication_adherence: "taken",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function toggleTrigger(t) {
    setForm((f) => ({
      ...f,
      triggers: f.triggers.includes(t) ? f.triggers.filter((x) => x !== t) : [...f.triggers, t],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/neurology/seizure", {
        ...form,
        duration_seconds: form.duration_seconds ? Number(form.duration_seconds) : null,
        post_ictal_minutes: form.post_ictal_minutes ? Number(form.post_ictal_minutes) : null,
        notes: form.notes || null,
      });
      toast.success("Seizure episode logged");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to log seizure");
    } finally { setSaving(false); }
  }

  const control = data.control;
  const controlColor = control ? (CONTROL_COLOR[control.band] || "#A3ACA7") : "#A3ACA7";

  return (
    <div className="space-y-6" data-testid="neuro-seizure-tab">
      {control && (
        <div className="ros-card" data-testid="seizure-control-card"
             style={{ borderColor: `${controlColor}55` }}>
          <div className="flex items-center gap-3">
            <ShieldAlert size={22} style={{ color: controlColor }} />
            <div>
              <div className="font-heading text-xl" style={{ color: controlColor }}>{control.band}</div>
              <div className="text-sm text-[#A3ACA7] mt-1">{control.recommendation}</div>
              <div className="text-xs text-[#7A8480] mt-2">
                {data.summary?.count_last_30d ?? 0} episode(s) in last 30 days ·
                {data.summary?.days_since_last != null
                  ? ` ${data.summary.days_since_last} day(s) since last`
                  : " no history"}
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="ros-card space-y-4" data-testid="seizure-form">
        <div className="font-heading text-lg">Log a seizure</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="seizure-input-date" className="ros-input" required />
          </Field>
          <Field label="Seizure type (ILAE 2017)">
            <select value={form.seizure_type}
                    onChange={(e) => setForm({ ...form, seizure_type: e.target.value })}
                    data-testid="seizure-input-type" className="ros-input">
              {SEIZURE_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
            </select>
          </Field>
          <Field label="Duration (seconds)">
            <input type="number" min={0} value={form.duration_seconds}
                   onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                   data-testid="seizure-input-duration" className="ros-input" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Post-ictal period (minutes)">
            <input type="number" min={0} value={form.post_ictal_minutes}
                   onChange={(e) => setForm({ ...form, post_ictal_minutes: e.target.value })}
                   data-testid="seizure-input-postictal" className="ros-input" />
          </Field>
          <Field label="Medication adherence">
            <select value={form.medication_adherence}
                    onChange={(e) => setForm({ ...form, medication_adherence: e.target.value })}
                    data-testid="seizure-input-adherence" className="ros-input">
              <option value="taken">Taken as prescribed</option>
              <option value="missed">Missed dose(s)</option>
              <option value="unknown">Unknown</option>
            </select>
          </Field>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Triggers</div>
          <div className="flex flex-wrap gap-2" data-testid="seizure-triggers">
            {SEIZURE_TRIGGER_LIST.map((t) => {
              const on = form.triggers.includes(t);
              return (
                <button type="button" key={t} onClick={() => toggleTrigger(t)}
                        data-testid={`seizure-trigger-${t}`}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          on ? "text-black border-transparent" : "text-[#A3ACA7] border-[#272E2B] hover:text-white"
                        }`}
                        style={on ? { background: ACCENT } : {}}>
                  {humanize(t)}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="seizure-input-notes"
                    className="ros-input" />
        </Field>

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="seizure-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="seizure-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Logging…" : "Log seizure"}
          </button>
        </div>
      </form>

      <HistoryTable
        title={`Seizure history (${data.episodes.length})`}
        testId="seizure-history"
        rows={data.episodes}
        rowTestId={(r) => `seizure-row-${r.id}`}
        empty="No seizure episodes logged."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Type", render: (r) => <span className="text-xs">{humanize(r.seizure_type)}</span> },
          { label: "Dur", render: (r) => <span className="font-mono">{r.duration_seconds ?? "—"}s</span> },
          { label: "Post-ictal", render: (r) => <span className="font-mono">{r.post_ictal_minutes ?? "—"}m</span> },
          { label: "Adherence", render: (r) => (
            <span className="text-xs" style={{ color: r.medication_adherence === "missed" ? "#F87171" : "#4ADE80" }}>
              {humanize(r.medication_adherence)}
            </span>
          )},
          { label: "Triggers", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {(r.triggers || []).slice(0, 3).map((t) =>
                <span key={t} className="text-[10px] text-[#A3ACA7] bg-[#1C201E] px-1.5 py-0.5 rounded">{humanize(t)}</span>
              )}
            </div>
          )},
        ]}
      />
    </div>
  );
}
